import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, User, LogOut, Rewind } from 'lucide-react';
import { NofeeNavLogo } from './NoFeeLogo';
import { useAuth } from '../context/AuthContext';
import OnboardingProgress from './OnboardingProgress';

const API = process.env.REACT_APP_BACKEND_URL;

// Mirrors STEPS in OnboardingProgress.jsx — kept here too so the shell can
// look up the route + label for the "skip ahead" resume banner without
// importing the array (which would create a circular concern). Order MUST
// match the visible stepper.
// iter141 — 5-step order (Account · Plan · Stripe · Trial · Go live).
const STEP_META = {
  account_created: { idx: 0, label: 'Create Account', route: '/onboarding/account' },
  choose_plan:     { idx: 1, label: 'Plan',           route: '/onboarding/plan' },
  stripe_connect:  { idx: 2, label: 'Stripe',         route: '/onboarding/stripe-connect' },
  subscribe:       { idx: 3, label: 'Trial',          route: '/onboarding/trial' },
  setup:           { idx: 4, label: 'Go live',        route: '/onboarding/setup' },
  // Legacy key collapses to the closest visible step.
  payout_trust:    { idx: 1, label: 'Plan',           route: '/onboarding/plan' },
  done:            { idx: 5, label: 'Done',           route: '/dashboard' },
};

/**
 * OnboardingShell — single source of truth for the navigation shell used by
 * every onboarding/setup/checkout flow on NoFee.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────┐
 *   │ [← Back]   [NoFee Logo]                  [Profile/Menu]│  sticky
 *   ├────────────────────────────────────────────────────────┤
 *   │ 1 Account · 2 Payouts · 3 Stripe · 4 Plan …            │  progress
 *   ├────────────────────────────────────────────────────────┤
 *   │                                                        │
 *   │            (page content via children)                 │
 *   │                                                        │
 *   └────────────────────────────────────────────────────────┘
 *
 * Behaviour:
 *  - Logo always on the LEFT, clickable. Authenticated restaurant owner →
 *    /dashboard, authenticated customer → /customer/rewards, public → /.
 *  - Back button uses history.back() when there is in-app history; otherwise
 *    falls back to the deterministic `backFallback` route prop. Never creates
 *    redirect loops thanks to the fallback.
 *  - Onboarding progress bar shown automatically when `currentStep` is provided.
 *  - Sticky on top across mobile + desktop; consistent spacing system-wide.
 */
const OnboardingShell = ({
  children,
  currentStep,           // 'account_created' | 'choose_plan' | 'stripe_connect' | 'setup' | 'done' | null
  showBack = true,
  backFallback = '/onboarding',
  showProgress = true,
  contentMaxWidth = 'max-w-3xl',
  testid = 'onboarding-shell',
  hideAuthChrome = false, // hides Sign out / dashboard chrome when the page is pre-auth (Step 1)
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, logout } = useAuth() || {};
  const isAuthed = Boolean(token);

  // iter116 — "Skip ahead" detection. When the owner is authenticated AND
  // navigates to a step that's AHEAD of their persisted backend cursor (e.g.
  // they're on /onboarding/setup but their cursor is still 'stripe_connect'),
  // we surface a soft banner offering to jump back to the unfinished step.
  // The page itself remains accessible — Step 3 already enforces its own
  // hard lock via the Stripe Connect gate (iter115); this banner is purely
  // informational so owners aren't confused about where they should be.
  const [persistedStep, setPersistedStep] = useState(null);
  useEffect(() => {
    if (!isAuthed) { setPersistedStep(null); return; }
    let cancelled = false;
    axios
      .get(`${API}/api/onboarding/state`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setPersistedStep(r.data?.step || null); })
      .catch(() => { if (!cancelled) setPersistedStep(null); });
    return () => { cancelled = true; };
  }, [isAuthed, token]);

  const resumeHint = useMemo(() => {
    if (!isAuthed || !persistedStep || !currentStep) return null;
    if (persistedStep === 'done') return null;
    const curIdx = STEP_META[currentStep]?.idx ?? 0;
    const persistedIdx = STEP_META[persistedStep]?.idx ?? 0;
    // Only surface when the user has JUMPED AHEAD of where they actually
    // left off. Going BACK to a completed step is fine and intentional
    // (review/edit) — no banner needed in that case.
    if (curIdx > persistedIdx) {
      const meta = STEP_META[persistedStep];
      return {
        label: meta?.label || 'previous step',
        route: meta?.route || '/onboarding',
        stepNumber: (meta?.idx ?? 0) + 1,
      };
    }
    return null;
  }, [isAuthed, persistedStep, currentStep]);

  // Resolve logo destination based on auth state.
  // Restaurant owners → dashboard. Public visitors → homepage.
  const logoDest = isAuthed ? '/dashboard' : '/';

  // Resolve back behaviour.
  const canGoBack =
    typeof window !== 'undefined' && (window.history?.length ?? 0) > 1;

  const onBack = () => {
    if (canGoBack && location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(backFallback);
    }
  };

  // Lock the body to prevent layout shift on iOS rubber-banding when sticky.
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white antialiased"
      data-testid={testid}
    >
      {/* Sticky top nav — same exact pixel positions on every page */}
      <header
        className="sticky top-0 z-30 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/[0.06]"
        data-testid="onboarding-shell-nav"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
          {/* LEFT — Back + Logo */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {showBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Back"
                data-testid="onboarding-shell-back"
                className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 text-white/70 hover:text-white text-[12.5px] font-semibold transition-all"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.3} />
                <span className="hidden sm:inline">Back</span>
              </button>
            )}
            <Link
              to={logoDest}
              data-testid="onboarding-shell-logo"
              className="flex items-center shrink-0"
              aria-label="NoFee — go to dashboard"
            >
              <NofeeNavLogo height={32} />
            </Link>
          </div>

          {/* RIGHT — Profile / Menu */}
          <div className="flex items-center gap-2">
            {isAuthed && !hideAuthChrome ? (
              <>
                <span
                  className="hidden sm:inline text-[12px] text-white/55 max-w-[160px] truncate"
                  data-testid="onboarding-shell-user-email"
                >
                  {user?.email || ''}
                </span>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  data-testid="onboarding-shell-profile-btn"
                  className="w-9 h-9 rounded-full border border-white/12 hover:border-yellow-500/40 bg-white/[0.03] hover:bg-yellow-500/[0.06] flex items-center justify-center transition-all"
                  aria-label="Your dashboard"
                  title="Your dashboard"
                >
                  <User className="w-4 h-4 text-white/75" strokeWidth={1.8} />
                </button>
                <button
                  type="button"
                  onClick={() => { logout?.(); navigate('/'); }}
                  data-testid="onboarding-shell-signout-btn"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 hover:border-rose-500/40 text-white/55 hover:text-rose-300 text-[12px] font-semibold transition-all"
                >
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/')}
                data-testid="onboarding-shell-home-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 text-white/70 hover:text-white text-[12px] font-semibold transition-all"
              >
                Home
              </button>
            )}
          </div>
        </div>
      </header>

      {/* iter116 — Skip-ahead resume banner.
          Shown when an authenticated owner jumps to a step beyond their
          persisted cursor. The page itself remains usable but the banner
          makes it 1-click to return to the unfinished step. */}
      {resumeHint && (
        <div
          data-testid="onboarding-resume-banner"
          className="border-b border-amber-400/20 bg-gradient-to-r from-amber-500/[0.10] via-amber-500/[0.05] to-transparent"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                <Rewind className="w-3.5 h-3.5 text-amber-300" />
              </div>
              <div className="min-w-0">
                <p className="text-[12px] sm:text-[12.5px] text-amber-200/90 font-semibold leading-snug">
                  You left off on Step {resumeHint.stepNumber} —{' '}
                  <span className="text-white">{resumeHint.label}</span>. Finish it to keep moving.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(resumeHint.route)}
              data-testid="onboarding-resume-cta"
              className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/50 text-amber-100 text-[12px] font-bold tracking-tight transition-all"
            >
              Resume Step {resumeHint.stepNumber}
            </button>
          </div>
        </div>
      )}

      {/* Progress bar — collapses cleanly when not shown */}
      {showProgress && currentStep && (
        <div className="bg-[#0A0A0A] border-b border-white/[0.04]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-5 pb-4">
            <OnboardingProgress currentStep={currentStep} />
          </div>
        </div>
      )}

      {/* Content slot */}
      <main className={`${contentMaxWidth} mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-24`}>
        {children}
      </main>
    </div>
  );
};

export default OnboardingShell;
